import React, { useState } from 'react';
import { Edit, Trash2, Users } from 'lucide-react';
import styled from 'styled-components';
import InfoTooltip from 'components/_UI/InfoTooltip';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

// Mesmos chips de status da tabela de pedidos (fundo claro, texto escuro e borda).
const STATUS_MAP = {
    accepted: { label: 'Aceito', bg: '#DCFCE7', text: '#166534', border: '#B7E5C9' },
    pending: { label: 'Pendente', bg: '#FEF3C7', text: '#92400E', border: '#F3DFAE' },
    rejected: { label: 'Recusado', bg: '#FEE2E2', text: '#B3261E', border: '#F5C2C7' },
};

function moreLabel(count) {
    return `+${count} ${count === 1 ? 'permissão' : 'permissões'}`;
}

/**
 * Quem tem acesso ao evento, no mesmo formato da tabela de pedidos e da lista de
 * clientes. Cada linha é `{ id, email, permissions, origin, status, onEdit?,
 * onDelete? }`: `permissions` é a lista de rótulos, `origin` é a equipe que dá o
 * acesso (ou `null` num convite direto). Linhas de equipe não têm ações — o
 * acesso delas se gerencia na equipe — e, sem nenhuma linha com ação, a coluna
 * "Ações" não aparece.
 *
 * A célula de permissões mostra a primeira e um "+N permissões" que abre a
 * lista inteira logo abaixo da linha: um acesso de painel inteiro chega com
 * dezenas de permissões e esconder o resto sem saída seria perder informação.
 * Sem moldura própria — vai dentro do painel da aba, como a lista de pedidos.
 */
export default function EventAccessTable({ rows }) {
    const [expandedId, setExpandedId] = useState(null);

    // Só há ação em convite direto; se todas as linhas vêm de equipe, a coluna
    // ficaria vazia e some.
    const hasActions = rows.some(row => row.onEdit || row.onDelete);

    function toggleRow(id) {
        setExpandedId(current => current === id ? null : id);
    }

    function renderOrigin(row) {
        if (!row.origin) {
            return <OriginText>Convite direto</OriginText>;
        }

        return (
            <OriginTeam title={row.origin}>
                <Users size={14} color={colors.night} />
                <span>{row.origin}</span>
            </OriginTeam>
        );
    }

    function renderPermissions(row, panelId) {
        const [first, ...rest] = row.permissions || [];

        if (!first) {
            return <Secondary>—</Secondary>;
        }

        return (
            <ChipsRow>
                <PermissionChip title={first}>{first}</PermissionChip>
                {rest.length > 0 && (
                    <MoreChip
                        type='button'
                        $expanded={expandedId === row.id}
                        aria-expanded={expandedId === row.id}
                        aria-controls={panelId}
                        title={rest.join(', ')}
                        onClick={() => toggleRow(row.id)}
                    >
                        {moreLabel(rest.length)}
                    </MoreChip>
                )}
            </ChipsRow>
        );
    }

    function renderAllPermissions(row, panelId) {
        return (
            <ExpandedContent id={panelId}>
                <ExpandedLabel>Todas as permissões deste acesso</ExpandedLabel>
                <ChipsRow>
                    {row.permissions.map(permission => (
                        <PermissionChip key={permission} title={permission}>{permission}</PermissionChip>
                    ))}
                </ChipsRow>
            </ExpandedContent>
        );
    }

    function renderActions(row) {
        if (!row.onEdit && !row.onDelete) {
            return null;
        }

        return (
            <Actions>
                {row.onEdit && (
                    <InfoTooltip text='Editar' type='none'>
                        <ActionButton onClick={row.onEdit} disabled={row.status === 'rejected'}>
                            <Edit color={row.status !== 'rejected' ? colors.primary : colors.nightLight} size={18} />
                        </ActionButton>
                    </InfoTooltip>
                )}
                {row.onDelete && (
                    <InfoTooltip text='Excluir' type='none'>
                        <ActionButton onClick={row.onDelete}>
                            <Trash2 color={colors.danger} size={18} />
                        </ActionButton>
                    </InfoTooltip>
                )}
            </Actions>
        );
    }

    return (
        <>
            <DesktopTable>
                <thead>
                    <tr>
                        <Th>Colaborador</Th>
                        <Th>Permissões</Th>
                        <Th>Origem do acesso</Th>
                        <Th>Status</Th>
                        {hasActions && <Th align='right'>Ações</Th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => {
                        const chip = STATUS_MAP[row.status] || STATUS_MAP.pending;
                        const expanded = expandedId === row.id && (row.permissions || []).length > 1;
                        const panelId = `access-panel-${row.id}`;

                        return (
                            <React.Fragment key={row.id}>
                                <Tr $expanded={expanded}>
                                    <Td><Name title={row.email}>{row.email}</Name></Td>
                                    <Td>{renderPermissions(row, panelId)}</Td>
                                    <Td>{renderOrigin(row)}</Td>
                                    <Td>
                                        <StatusBadge $chip={chip}>{chip.label}</StatusBadge>
                                    </Td>
                                    {hasActions && <Td align='right'>{renderActions(row)}</Td>}
                                </Tr>

                                {expanded && (
                                    <ExpandedTr>
                                        <ExpandedTd colSpan={hasActions ? 5 : 4}>
                                            {renderAllPermissions(row, panelId)}
                                        </ExpandedTd>
                                    </ExpandedTr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </DesktopTable>

            <MobileCards>
                {rows.map(row => {
                    const chip = STATUS_MAP[row.status] || STATUS_MAP.pending;
                    const expanded = expandedId === row.id && (row.permissions || []).length > 1;
                    const panelId = `access-card-${row.id}`;

                    return (
                        <MobileCard key={row.id} $expanded={expanded}>
                            <MobileHeader>
                                <MobileHeaderText>
                                    <Name title={row.email}>{row.email}</Name>
                                </MobileHeaderText>
                                <StatusBadge $chip={chip}>{chip.label}</StatusBadge>
                            </MobileHeader>

                            <MobileBody>
                                <MobileField $stacked>
                                    <MobileLabel>Permissões</MobileLabel>
                                    {renderPermissions(row, panelId)}
                                </MobileField>
                                <MobileField>
                                    <MobileLabel>Origem</MobileLabel>
                                    {renderOrigin(row)}
                                </MobileField>
                                {renderActions(row) && (
                                    <MobileField>
                                        <MobileLabel>Ações</MobileLabel>
                                        {renderActions(row)}
                                    </MobileField>
                                )}
                            </MobileBody>

                            {expanded && (
                                <MobileExpanded>
                                    {renderAllPermissions(row, panelId)}
                                </MobileExpanded>
                            )}
                        </MobileCard>
                    );
                })}
            </MobileCards>
        </>
    );
}

/* ── Tabela (desktop) ──────────────────────────── */

const DesktopTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    display: none;
    margin-top: 12px;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: table;
    }
`;

const Th = styled.th`
    text-align: ${({ align }) => align || 'left'};
    padding: 12px 14px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 1px solid ${colors.neutralDark};
    white-space: nowrap;

    &:first-child { padding-left: 20px; }
    &:last-child { padding-right: 20px; }
`;

const Tr = styled.tr`
    transition: background 0.15s ease;
    border-bottom: 1px solid ${colors.neutralDark};
    background: ${({ $expanded }) => $expanded ? '#FAF6FF' : 'transparent'};

    &:hover {
        background: ${({ $expanded }) => $expanded ? '#FAF6FF' : colors.neutral};
    }
`;

const Td = styled.td`
    padding: 12px 14px;
    vertical-align: middle;
    text-align: ${({ align }) => align || 'left'};

    &:first-child { padding-left: 20px; }
    &:last-child { padding-right: 20px; }
`;

const ExpandedTr = styled.tr`
    border-bottom: 1px solid ${colors.neutralDark};
    background: #FAF6FF;
`;

const ExpandedTd = styled.td`
    padding: 0 20px 16px;
`;

/* ── Conteúdo das células ──────────────────────── */

const Name = styled.span`
    display: block;
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Secondary = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;

const OriginText = styled.span`
    font-size: 13px;
    color: ${colors.nightDark};
`;

const OriginTeam = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${colors.nightDark};
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 3px 11px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    white-space: nowrap;
    background: ${({ $chip }) => $chip.bg};
    color: ${({ $chip }) => $chip.text};
    border: 1px solid ${({ $chip }) => $chip.border};
`;

const Actions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 12px;
`;

const ActionButton = styled.div`
    transition: all ease-in-out .25s;
    cursor: pointer;
    display: flex;

    ${({ disabled }) => disabled && `
        opacity: .4;
        pointer-events: none;
    `};
`;

/* ── Chips de permissão (mesmo desenho dos chips de evento em Clientes) ── */

const ChipsRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
`;

const PermissionChip = styled.span`
    display: inline-flex;
    align-items: center;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 3px 10px;
    border-radius: 50px;
    font-size: 12px;
    font-weight: 600;
    background: ${colors.neutralBackground};
    border: 1px solid ${colors.neutralDark};
    color: ${colors.nightDark};
`;

const MoreChip = styled.button`
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    padding: 3px 10px;
    border: none;
    border-radius: 50px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    background: ${({ $expanded }) => panelAccentAlpha(colors.occasionalPurple, $expanded ? '25' : '10')};
    color: ${panelAccent(colors.occasionalPurple)};
    transition: background 0.15s ease;

    &:hover {
        background: ${panelAccentAlpha(colors.occasionalPurple, '25')};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: 2px;
    }
`;

const ExpandedContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 4px;
`;

const ExpandedLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
`;

/* ── Cards (mobile) ────────────────────────────── */

const MobileCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const MobileCard = styled.div`
    border: 1px solid ${({ $expanded }) => $expanded ? '#DFC8F8' : colors.neutralDark};
    border-radius: 10px;
    overflow: hidden;
    background: ${({ $expanded }) => $expanded ? '#FAF6FF' : colors.neutralLight};
    transition: all 0.15s ease;
`;

const MobileHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const MobileHeaderText = styled.span`
    flex: 1;
    min-width: 0;
`;

const MobileBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 14px;
`;

const MobileField = styled.div`
    display: flex;
    align-items: ${({ $stacked }) => $stacked ? 'flex-start' : 'center'};
    justify-content: space-between;
    gap: 12px;
`;

const MobileLabel = styled.span`
    flex-shrink: 0;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
`;

const MobileExpanded = styled.div`
    padding: 0 14px 14px;
`;
